import {
  BackrsFormValues,
  BackrsUser,
  handleBasicRegistrationWithError,
  ProfileOptionAnswer,
  RegistrationValues,
} from '@backrs_ui/common-ui';

import {
  OnboardingFlow,
  OnboardingQuestion,
  ONBOARDING_CONFIGS,
} from '@backrs_ui/grow-ui/src/pages/Onboarding/onboarding.types';
import { ENVIRONMENT_URL } from '@backrs_ui/grow-ui/src/settings';

import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';

type AutocompleteResultItem = Record<string, string>;

type AutocompleteResponse = Readonly<{
  results: AutocompleteResultItem[];
}>;

// Define the return type for the autocomplete query
export type AutocompleteResult = Readonly<{
  endpoint: string;
  values: {
    label: string;
    value: string;
  }[];
  searchTerm?: string;
}>;

type ApiFieldError = Readonly<{
  value: string;
  msg: string;
  param: string;
  location: string;
}>;

type ApiErrorResponse = Readonly<{
  errors: ApiFieldError[];
  message: string;
}>;

export type ProfileOptionAnswerBatchPostRequest = Readonly<Record<string, ProfileOptionAnswer[]>>;

export const onboardingApi = createApi({
  reducerPath: 'onboardingApi',
  baseQuery: fakeBaseQuery(), // tell RTK Query this is a fake local query for now - will be replaced with a real API call eventually
  endpoints: (builder) => ({
    fetchOnboardingQuestions: builder.query<Record<string, OnboardingQuestion>, OnboardingFlow>({
      queryFn: async (onboardingType) => {
        if (!onboardingType || !(onboardingType in ONBOARDING_CONFIGS)) {
          return { error: { status: 400, data: 'Invalid onboardingType' } };
        }

        const questions = ONBOARDING_CONFIGS[onboardingType]();

        return { data: questions };
      },
    }),
    fetchAutocompleteOptions: builder.query<
      AutocompleteResult[],
      { endpoint: string; searchTerm: string }
    >({
      queryFn: async ({ endpoint, searchTerm }) => {
        try {
          if (!endpoint || !searchTerm || searchTerm.length < 4) {
            // Full text search has to be at least 4 characters
            return { data: [] };
          }

          const response = await fetch(
            `${ENVIRONMENT_URL}${endpoint}?q=${encodeURIComponent(searchTerm)}`,
          );

          if (!response.ok) {
            throw new Error('Failed to fetch autocomplete options');
          }

          const data: AutocompleteResponse = await response.json();

          // Extract all values into a single array
          const values = data.results
            .map((item) => {
              // Extract the first non-empty value from the item
              for (const key in item) {
                if (item[key]) {
                  return { label: item[key], value: item[key] };
                }
              }

              return { label: '', value: '' };
            })
            .filter(Boolean);

          const formattedData = {
            endpoint,
            values,
          };

          return { data: [formattedData] };
        } catch (error) {
          return {
            error: {
              status: 'FETCH_ERROR',
              data: error instanceof Error ? error.message : 'Unknown error',
            },
          };
        }
      },
    }),
    postUser: builder.mutation<{ user: BackrsUser }, { userData: BackrsFormValues }>({
      async queryFn({ userData }) {
        try {
          const registerData: RegistrationValues = {
            first_name: userData.first_name as string,
            last_name: userData.last_name as string,
            phone: userData.phone as string,
            email: userData.email as string,
            password: userData.password as string,
            is_fourteen_plus: true,
            register_id: null,
            invite_code: userData.invite_code as string,
            zip_code: userData.zip_code as string,
            grade: userData.grade as string,
            school: userData.school as string,
          };

          const { user } = await handleBasicRegistrationWithError(registerData, ENVIRONMENT_URL);

          return { data: { user } };
        } catch (err) {
          const error = err as { response?: ApiErrorResponse; message?: string };

          const extractedErrors = error.response?.errors?.reduce(
            (acc: Record<string, string>, curr: ApiFieldError) => {
              if (curr.param && curr.msg) {
                acc[curr.param] = curr.msg;
              }

              return acc;
            },
            {},
          ) || { general: error.message || 'Unknown error' };

          return {
            error: {
              status: 400,
              data: extractedErrors,
            },
          };
        }
      },
    }),

    postProfileOptionAnswer: builder.mutation<
      { success: boolean },
      {
        profileOptionAnswers: ProfileOptionAnswer[];
        access_token: string;
        postKey: string;
      }
    >({
      queryFn: async ({ profileOptionAnswers, access_token, postKey }) => {
        try {
          const body: ProfileOptionAnswerBatchPostRequest = {
            [postKey]: profileOptionAnswers,
          };

          const response = await fetch(`${ENVIRONMENT_URL}/profile`, {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${access_token}`,
            },
            body: JSON.stringify(body),
          });

          if (!response.ok) {
            throw new Error(`Failed to patch question_id: ${postKey}`);
          }

          return { data: { success: true } };
        } catch (error) {
          return {
            error: {
              status: 'POST_ERROR',
              data: error instanceof Error ? error.message : 'Unknown error',
            },
          };
        }
      },
    }),
  }),
});

export const {
  useFetchOnboardingQuestionsQuery,
  useLazyFetchAutocompleteOptionsQuery,
  usePostUserMutation,
  usePostProfileOptionAnswerMutation,
  util: { resetApiState },
} = onboardingApi;
